/**
 * `<IssueNewVersionModal>` — SA two-step modal for issuing a new bias
 * guardrails version (B-22b, HoD §2d).
 *
 * Step 1: textarea pre-filled with current text + required issue_reason +
 *         collapsible diff preview. Submit = "Issue v{N+1}" (amber-tone,
 *         NOT destructive red).
 * Step 2: confirmation swap "This will become effective immediately for
 *         every tenant's next AI call. There is no rollback…". Final Issue
 *         button.
 *
 * The amber tone (not red) is per HoD §2d — issuing a new version is
 * additive (history is preserved); "destructive" red is reserved for
 * irreversible deletes.
 *
 * HoD acceptance gate #8: modal cannot submit from step 1.
 */
import { useState, type JSX } from "react";
import { Button } from "@/components/common/Button";

interface IssueNewVersionModalProps {
  isOpen: boolean;
  currentText: string;
  currentVersion: number;
  onIssue: (text: string, reason: string) => Promise<void>;
  onCancel: () => void;
}

export function IssueNewVersionModal({
  isOpen,
  currentText,
  currentVersion,
  onIssue,
  onCancel,
}: IssueNewVersionModalProps): JSX.Element | null {
  const [step, setStep] = useState<1 | 2>(1);
  const [text, setText] = useState(currentText);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const dirty = text !== currentText;
  const canProceedFromStep1 = dirty && reason.trim().length > 0;

  const handleProceedToStep2 = () => {
    if (!canProceedFromStep1) return;
    setStep(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onIssue(text, reason);
      // Parent handles success — close, refresh, etc.
    } catch (e) {
      const err = e as { response?: { data?: { detail?: { message?: string } } } };
      setError(err.response?.data?.detail?.message ?? "Failed to issue new version.");
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-version-title"
      data-testid="issue-new-version-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="w-full max-w-2xl rounded-xl border border-border-default bg-surface-elevated p-5 shadow-2xl dark:bg-surface-card">
        <h2
          id="issue-version-title"
          className="mb-4 text-lg font-medium text-text-primary"
        >
          Issue new bias guardrails version
        </h2>

        {step === 1 ? (
          <div className="space-y-3" data-testid="issue-step-1">
            <label className="block text-sm font-medium text-text-secondary">
              Guardrails text
            </label>
            <textarea
              className="min-h-[400px] w-full rounded-md border border-border-default bg-surface-canvas p-3 font-mono text-sm text-text-primary dark:bg-[var(--p-navy-940)]"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="issue-textarea"
            />
            <label className="block text-sm font-medium text-text-secondary">
              Issue reason (required)
            </label>
            <input
              type="text"
              className="w-full rounded-md border border-border-default bg-surface-canvas p-2 text-sm text-text-primary dark:bg-[var(--p-navy-940)]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What changed and why?"
              data-testid="issue-reason-input"
            />
            <p className="text-xs text-text-secondary">
              Visible in version history and audit log.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={onCancel} data-modal-safe>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleProceedToStep2}
                disabled={!canProceedFromStep1}
                data-testid="issue-step1-proceed"
              >
                Issue v{currentVersion + 1}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="issue-step-2">
            <div
              className="rounded-md border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
              data-testid="issue-warning-banner"
            >
              <p className="font-medium">This will become effective immediately.</p>
              <p className="mt-1">
                Every tenant&apos;s next AI call will use the new locked text.
                There is no rollback; you must issue a new version to change it.
                Continue?
              </p>
            </div>
            {error ? (
              <div
                role="alert"
                className="border-l-4 border-status-rejected-fg bg-status-rejected-bg px-3 py-2 text-sm"
                data-testid="issue-error"
              >
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setStep(1)}
                disabled={submitting}
                data-modal-safe
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSubmit()}
                disabled={submitting}
                data-testid="issue-step2-confirm"
              >
                {submitting ? "Issuing…" : `Issue v${currentVersion + 1}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
